import { onMounted, onBeforeUnmount, watch } from 'vue'
import { useWsStore, type SessionID } from '@/stores/ws'
import { useRouter } from 'vue-router'

/**
 * Simplified composable that uses the consolidated activity tracking from the websocket store
 * Automatically handles all activity detection (idle, focus, online, visibility, etc.)
 */
export function useSessionActivity(sessionId: () => number | null) {
  const ws = useWsStore()
  const router = useRouter()

  let lastTrackedId: number | null = null
  let unwatchSessionId: (() => void) | null = null
  let unsubscribeRouter: (() => void) | null = null

  function startTrackingIfReady(id: number | null) {
    if (id !== null && id !== lastTrackedId) {
      // Stop previous tracking if any
      if (lastTrackedId !== null && lastTrackedId !== id) {
        ws.stopActivityTracking(lastTrackedId as SessionID)
      }
      ws.startActivityTracking(id as SessionID)
      lastTrackedId = id
    }
  }

  function stopTrackingCurrent() {
    if (lastTrackedId !== null) {
      ws.stopActivityTracking(lastTrackedId as SessionID)
      lastTrackedId = null
    }
  }

  onMounted(() => {
    // Initial attempt
    startTrackingIfReady(sessionId())

    // React to sessionId availability changes (e.g., after API creates one)
    unwatchSessionId = watch(
      () => sessionId(),
      (id) => {
        if (id !== null) {
          startTrackingIfReady(id)
        }
      },
    )

    // Listen for route changes to stop tracking when leaving tracked pages
    const removeGuard = router.beforeEach((to, from) => {
      const isLeavingTrackedPage = from.path === '/countdown' || from.path === '/control'
      const isStayingOnTrackedPage = to.path === '/countdown' || to.path === '/control'
      if (isLeavingTrackedPage && !isStayingOnTrackedPage) {
        stopTrackingCurrent()
      }
      return true
    })
    // Vue Router returns a remove function in v4
    unsubscribeRouter = typeof removeGuard === 'function' ? removeGuard : null
  })

  onBeforeUnmount(() => {
    stopTrackingCurrent()

    if (unsubscribeRouter) {
      unsubscribeRouter()
      unsubscribeRouter = null
    }
    if (unwatchSessionId) {
      unwatchSessionId()
      unwatchSessionId = null
    }
  })

  return {
    // Expose activity state for UI feedback if needed
    isActive: () => ws.isUserActive(),
    isIdle: ws.isIdle,
    isWindowFocused: ws.isWindowFocused,
    isOnline: ws.isOnline,
  }
}
